
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase, fromTable } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { feedbackSchema, FeedbackFormValues, FeedbackFormProps } from './schema';

export const useFeedbackForm = ({ initialData, onSubmitSuccess, isRecruiter }: FeedbackFormProps) => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState<Array<{id: string, name: string}>>([]);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '' });

  const isReadOnly = !!initialData?.requestId;
  const requestData = initialData || location.state || {};

  const getContextSpecificLabels = () => {
    const contextType = requestData.contextType || 'recrutement';
    
    let candidateLabel = 'Candidat';
    let positionLabel = 'Intitulé du poste';
    
    if (contextType === 'mission') {
      candidateLabel = 'Freelance';
      positionLabel = 'Nom du projet';
    } else if (contextType === 'managérial' || contextType === 'collaboration') {
      candidateLabel = 'Collègue';
      positionLabel = 'Poste / Position';
    }
    
    return { candidateLabel, positionLabel };
  };
  
  const contextLabels = getContextSpecificLabels();

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      candidateName: requestData.candidateName || '',
      candidateEmail: requestData.candidateEmail || '',
      jobTitle: requestData.jobTitle || '',
      positives: '',
      improvements: '',
      generalComment: '',
      finalMessage: '',
      certified: false,
      projectId: requestData.projectId || '',
    },
  });

  // Charger les projets de l'utilisateur
  const loadProjects = async () => {
    if (user) {
      try {
        const { data, error } = await fromTable('projects')
          .select('id, name')
          .eq('user_id', user.id)
          .order('name', { ascending: true });
          
        if (error) throw error;
        if (data) setProjects(data);
      } catch (error) {
        console.error('Erreur lors du chargement des projets:', error);
      }
    }
  };

  // Créer un nouveau projet
  const createProject = async () => {
    if (!user || !newProject.name.trim()) {
      toast.error("Veuillez entrer un nom de projet");
      return;
    }
    
    try {
      const { data, error } = await fromTable('projects')
        .insert({
          name: newProject.name,
          description: newProject.description,
          user_id: user.id,
          category: 'custom'
        })
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        setProjects([...projects, { id: data[0].id, name: data[0].name }]);
        form.setValue('projectId', data[0].id);
        setNewProject({ name: '', description: '' });
        setShowNewProject(false);
        toast.success("Projet créé avec succès");
      }
    } catch (error) {
      console.error('Erreur lors de la création du projet:', error);
      toast.error("Erreur lors de la création du projet");
    }
  };

  // Charger les projets au chargement du composant
  useState(() => {
    loadProjects();
  });

  const onSubmit = async (data: FeedbackFormValues) => {
    if (!user) {
      toast.error("Erreur", {
        description: "Vous devez être connecté pour effectuer cette action",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      console.log("Submitting feedback with data:", data);
      console.log("Is recruiter feedback:", isRecruiter);
      console.log("Request data:", requestData);
      console.log("Context type:", requestData.contextType);
      console.log("Direction type:", requestData.directionType);
      console.log("Project ID:", data.projectId);

      let feedbackId: string | null = null;

      if (isRecruiter) {
        const feedbackData = {
          candidate_user_id: requestData?.candidateUserId || null,
          recruiter_user_id: user?.id || requestData?.recruiterUserId || null,
          feedback_text: JSON.stringify({
            positives: data.positives,
            improvements: data.improvements,
            generalComment: data.generalComment,
            finalMessage: data.finalMessage,
            jobTitle: data.jobTitle,
            contextType: requestData.contextType,
            directionType: requestData.directionType,
            requestData: requestData,
          }),
          rating: 5,
          is_anonymous: false,
        };

        console.log("Submitting to candidate_feedbacks with data:", feedbackData);

        const { data: feedbackResult, error } = await supabase
          .from('candidate_feedbacks')
          .insert(feedbackData)
          .select();

        if (error) {
          console.error("Error submitting candidate feedback:", error);
          throw error;
        }

        console.log("Candidate feedback submitted successfully:", feedbackResult);
        feedbackId = feedbackResult[0].id;

        if (requestData?.requestId && requestData?.tableToUse) {
          console.log(`Updating request ${requestData.requestId} with feedback ID ${feedbackId}`);
          
          const updateData = {
            status: 'completed',
            message: JSON.stringify({
              feedback_id: feedbackId,
              feedback_type: 'recruiter',
              updated_at: new Date().toISOString()
            })
          };
          
          const { error: updateError } = await supabase
            .from(requestData.tableToUse)
            .update(updateData)
            .eq('id', requestData.requestId);
          
          if (updateError) {
            console.error('Error updating request status:', updateError);
            toast.warning("Feedback enregistré mais la mise à jour du statut de la demande a échoué");
          } else {
            console.log("Request status updated successfully");
          }
        }
      } else {
        const feedbackType = requestData.directionType 
          ? requestData.directionType.split('_')[0]
          : profile?.role === 'candidat' ? 'candidat' : 'recruiter';
        
        console.log("Using feedback type:", feedbackType);
        
        const { data: feedbackResult, error } = await supabase.from('feedbacks').insert({
          user_id: user.id,
          candidate_name: form.getValues('candidateName'),
          candidate_email: form.getValues('candidateEmail'),
          job_title: form.getValues('jobTitle'),
          positives: form.getValues('positives'),
          improvements: form.getValues('improvements'),
          general_comment: form.getValues('generalComment'),
          final_message: form.getValues('finalMessage'),
          certified: form.getValues('certified'),
          feedback_type: feedbackType,
          context_type: requestData.contextType || 'recrutement',
          direction_type: requestData.directionType || 'candidate_to_recruiter',
        }).select();

        if (error) throw error;
        
        if (feedbackResult && feedbackResult.length > 0) {
          feedbackId = feedbackResult[0].id;
        }
      }
      
      if (data.projectId && feedbackId) {
        const { error: projectError } = await fromTable('projects_feedbacks')
          .insert({
            project_id: data.projectId,
            feedback_id: feedbackId,
            user_id: user.id
          });
        
        if (projectError) {
          console.error('Error linking feedback to project:', projectError);
          toast.warning("Feedback enregistré mais l'association au projet a échoué");
        } else {
          console.log("Feedback successfully associated with project");
        }
      }
      
      if (requestData.projectId && feedbackId && requestData.projectId !== data.projectId) {
        const { error: projectError } = await fromTable('projects_feedbacks')
          .insert({
            project_id: requestData.projectId,
            feedback_id: feedbackId,
            user_id: user.id
          });
        
        if (projectError) {
          console.error('Error linking feedback to project:', projectError);
          toast.warning("Feedback enregistré mais l'association au projet a échoué");
        }
      }

      toast.success("Merci pour votre feedback !", {
        description: "Votre avis a été enregistré avec succès."
      });

      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        navigate('/dashboard');
      }

    } catch (error: any) {
      console.error("Erreur soumission feedback :", error);
      toast.error("Erreur", {
        description: error.message || "Une erreur est survenue lors de l'envoi du feedback",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (tab: string) => setCurrentTab(tab);

  const goToNextTab = () => {
    const tabOrder = ['details', 'positives', 'improvements', 'final'];
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex < tabOrder.length - 1) {
      setCurrentTab(tabOrder[currentIndex + 1]);
    }
  };

  const goToPreviousTab = () => {
    const tabOrder = ['details', 'positives', 'improvements', 'final'];
    const currentIndex = tabOrder.indexOf(currentTab);
    if (currentIndex > 0) {
      setCurrentTab(tabOrder[currentIndex - 1]);
    }
  };

  return {
    form,
    currentTab,
    isSubmitting,
    isReadOnly,
    requestData,
    contextLabels,
    projects,
    showNewProject,
    setShowNewProject,
    newProject,
    setNewProject,
    createProject,
    loadProjects,
    handleTabChange,
    goToNextTab,
    goToPreviousTab,
    onSubmit,
  };
};
